import React, { useRef, useState, useEffect } from 'react';
import { logError } from '../api';

const PhotoCaptureModal = ({ isOpen, onClose, onPhotoTaken }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [photo, setPhoto] = useState(null);
  const [facingMode, setFacingMode] = useState('environment');

  const startCamera = async (facing = facingMode) => {
    try {
      setPhoto(null);
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: facing } });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
    } catch (error) {
      logError(error);
      console.error('Error al acceder a la cámara:', error);
      alert('No se pudo acceder a la cámara.');
    }
  };

  const stopCamera = () => {
    try {
      if (videoRef.current && videoRef.current.srcObject) {
        const stream = videoRef.current.srcObject;
        const tracks = stream.getTracks();
        
        // Detener cada track
        tracks.forEach((track) => {
          track.stop(); // Detener cada track
        });
        
        videoRef.current.srcObject = null; // Limpieza del srcObject
      }
    } catch (error) {
      logError(error);
      console.error('Error al detener la cámara:', error);
    }
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (video && canvas) {
      const context = canvas.getContext('2d');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      const dataURL = canvas.toDataURL('image/png');
      setPhoto(dataURL);
      onPhotoTaken(dataURL); // Notifica al componente padre
      stopCamera(); // Detenemos la cámara al cerrar el modal
  }
  };

  const handleClose = () => {
    stopCamera(); // Detenemos la cámara al cerrar el modal
    setPhoto(null);
    onClose();
  };

  const switchCamera = () => {
    const newFacing = facingMode === 'environment' ? 'user' : 'environment';
    setFacingMode(newFacing);
    stopCamera();
    startCamera(newFacing);
  };

  useEffect(() => {
    if (isOpen) {
      startCamera();
    } else {
      stopCamera(); // Asegura detener la cámara cuando el modal se cierra
    }
    return () => stopCamera(); // Garantiza limpieza al desmontar el componente
  }, [isOpen]);

  return (
    <div
      className={`modal fade ${isOpen ? 'show' : ''}`}
      style={{ display: isOpen ? 'block' : 'none' }}
      tabIndex="-1"
      role="dialog"
    >
      <div className="modal-dialog" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Captura de Foto</h5>
            <button type="button" className="close" aria-label="Close" onClick={handleClose}>
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div className="modal-body text-center">
            {photo ? (
              <>
                <img src={photo} alt="Captura" className="img-fluid" />
                <button className="btn btn-primary mt-3" onClick={() => setPhoto(null)}>
                  Tomar otra foto
                </button>
              </>
            ) : (
              <>
                <video ref={videoRef} className="w-100 mb-3" />
                <div className="d-flex gap-2 justify-content-center">
                  <button className="btn btn-success" onClick={capturePhoto}>
                    Capturar Foto
                  </button>
                  <button className="btn btn-outline-secondary" onClick={switchCamera}>
                    🔄 Cambiar cámara
                  </button>
                </div>
              </>
            )}
            <canvas ref={canvasRef} style={{ display: 'none' }} />
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={handleClose}>
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhotoCaptureModal;
